import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { approvalFilter, MyInfoState } from '@/recoil/atom';
import { ApprovalFilterType } from '@/types/api';
import { useAuthorStatusBadge } from '@/hooks/apiHooks/approval';

/**
 * 전자결재 SNB '완료 문서'/'반려 문서'의 빨간 점(dot) 뱃지 상태.
 *
 * status-badge 엔드포인트는 완료(C)/반려(R) '전체' 카운트를 내려주므로(자가결재는 서버에서 제외),
 * 알림 뱃지처럼 "마지막으로 본 시점 이후 새 완료/반려가 생겼을 때만" 점을 켠다.
 *  - lastSeen 카운트는 사용자별 localStorage 에 보존.
 *  - 저장된 lastSeen 이 없으면(=배포 후 첫 방문) 현재 카운트를 기준선으로 잡아 저장한다 → 점 미노출.
 *    이렇게 해야 배포 직후 이미 쌓여 있던 완료/반려 문서로 모든 사용자에게 점이 뜨지 않고,
 *    "배포 이후 새로 생긴 완료/반려"부터만 점이 켜진다.
 *  - 현재 카운트 > lastSeen 이면 점 노출(= 미확인 신규 존재).
 *  - 사용자가 '완료 문서'/'반려 문서' 필터를 선택하면 해당 상태의 lastSeen 을 현재 카운트로 갱신 → 점 소멸.
 */

type SeenCount = { completed: number; rejected: number };

const STORAGE_PREFIX = 'approval-author-status-badge-seen';

const storageKey = (passportId: string) => `${STORAGE_PREFIX}-${passportId}`;

/** 저장된 기준선을 반환. 없으면 null(= 아직 기준선 미설정 → 첫 방문 시 현재 카운트로 캡처). */
const readSeen = (passportId: string): SeenCount | null => {
  if (typeof window === 'undefined' || !passportId) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(passportId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SeenCount>;
    return {
      completed: parsed.completed ?? 0,
      rejected: parsed.rejected ?? 0,
    };
  } catch {
    return null;
  }
};

const writeSeen = (passportId: string, seen: SeenCount) => {
  if (typeof window === 'undefined' || !passportId) return;
  try {
    window.localStorage.setItem(storageKey(passportId), JSON.stringify(seen));
  } catch {
    // 저장 실패는 무시 — 뱃지 소멸이 다음 방문에 유지되지 않을 뿐 기능은 동작.
  }
};

const useAuthorStatusBadgeDot = () => {
  const { passportId } = useAtomValue(MyInfoState);
  const { id: selectedId } = useAtomValue(approvalFilter);
  const { data } = useAuthorStatusBadge();

  const completedCount = data?.completed?.count ?? 0;
  const rejectedCount = data?.rejected?.count ?? 0;
  const isLoaded = !!data;

  // null = 기준선 미설정(저장값 없음 & 아직 캡처 전). 이 동안에는 점을 노출하지 않는다.
  const [seen, setSeen] = useState<SeenCount | null>(null);

  // passportId 확정 후 저장된 기준선 복원(SSR/최초 마운트 대응).
  useEffect(() => {
    setSeen(readSeen(passportId));
  }, [passportId]);

  const markSeen = useCallback(
    (key: keyof SeenCount, count: number) => {
      setSeen((prev) => {
        const base = prev ?? { completed: 0, rejected: 0 };
        if (base[key] === count) return prev;
        const next = { ...base, [key]: count };
        writeSeen(passportId, next);
        return next;
      });
    },
    [passportId],
  );

  // 저장된 기준선이 없으면(배포 후 첫 방문) 현재 카운트를 기준선으로 캡처 → 기존 누적분은 점 미노출.
  useEffect(() => {
    if (!isLoaded || seen !== null) return;
    const baseline: SeenCount = {
      completed: completedCount,
      rejected: rejectedCount,
    };
    writeSeen(passportId, baseline);
    setSeen(baseline);
  }, [isLoaded, seen, completedCount, rejectedCount, passportId]);

  // 완료/반려 필터를 보고 있는 동안에는 신규 카운트를 즉시 '확인함'으로 간주 → 점 소멸.
  useEffect(() => {
    if (!isLoaded || seen === null) return;
    if (selectedId === ApprovalFilterType['완료 문서']) {
      markSeen('completed', completedCount);
    } else if (selectedId === ApprovalFilterType['반려 문서']) {
      markSeen('rejected', rejectedCount);
    }
  }, [selectedId, isLoaded, seen, completedCount, rejectedCount, markSeen]);

  return useMemo(
    () => ({
      showCompletedDot: seen !== null && completedCount > seen.completed,
      showRejectedDot: seen !== null && rejectedCount > seen.rejected,
    }),
    [completedCount, rejectedCount, seen],
  );
};

export default useAuthorStatusBadgeDot;
